/**
 * The one editor for a `window` slot or field (SA-1136; spec §6.3). Mounted by `FieldInput`
 * for `type: window`. Provider-free: no fetch, no store. The words are the customer's, shown
 * on a read-only line once the window has words; a window that had none when the editor
 * opened (a new record, an empty field, a failed extraction) takes typed words, and stays
 * editable for the whole edit (ruling P40). A person sets the two dates and the flexibility,
 * and the window is theirs (`entered`) the moment they touch a date. New words drop dates read
 * from the old words (SA-1138 W5). The described-by reaches every control a person edits. A
 * guessed window (parsed from the words on the server) carries a chip and a Confirm.
 */
import { useState } from 'react'
import { Badge, Button, Input, NativeSelect } from '@/components/ui'
import {
  confirmedWindow, FLEXIBILITIES, isWindow, legacyWindow, windowState, windowWords, withDates, withFlexibility,
  withWords, type Flexibility, type WindowSlotValue,
} from '@/components/records/window'
import {
  AS_WRITTEN, CONFIRM, EARLIEST, FLEXIBILITY, FLEXIBILITY_WORDS, GUESSED, LATEST, WORDS_PLACEHOLDER,
} from '@/components/records/windowText'

function current(value: unknown): WindowSlotValue | null {
  if (isWindow(value)) return value
  if (typeof value === 'string') return legacyWindow(value)
  return null
}

export function WindowInput({ id, value, onChange, describedBy }: {
  id: string
  value: unknown
  onChange: (v: WindowSlotValue | null) => void
  describedBy?: string
}) {
  const w = current(value)
  const guessed = w !== null && windowState(w) === 'guessed'
  // Read once at mount: typing never turns the line read-only mid-edit (ruling P40).
  const [typable] = useState(() => windowWords(value) === '')
  return (
    <div className="space-y-2">
      {typable ? (
        <Input id={id} value={w?.text ?? ''} aria-describedby={describedBy} placeholder={WORDS_PLACEHOLDER}
          onChange={(e) => onChange(withWords(w, e.target.value))} />
      ) : (
        <Input id={id} readOnly value={w?.text ?? ''} aria-describedby={describedBy} />
      )}
      <p className="text-xs text-muted-foreground">{AS_WRITTEN}</p>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label htmlFor={`${id}-earliest`} className="text-xs text-muted-foreground">{EARLIEST}</label>
          <Input id={`${id}-earliest`} type="date" value={w?.earliest ?? ''} aria-describedby={describedBy}
            onChange={(e) => onChange(withDates(w, e.target.value || null, w?.latest ?? null))} />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-latest`} className="text-xs text-muted-foreground">{LATEST}</label>
          <Input id={`${id}-latest`} type="date" value={w?.latest ?? ''} aria-describedby={describedBy}
            onChange={(e) => onChange(withDates(w, w?.earliest ?? null, e.target.value || null))} />
        </div>
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-flexibility`} className="text-xs text-muted-foreground">{FLEXIBILITY}</label>
        <NativeSelect id={`${id}-flexibility`} value={w?.flexibility ?? 'unknown'} disabled={w === null}
          aria-describedby={describedBy}
          onChange={(e) => onChange(withFlexibility(w, e.target.value as Flexibility))}>
          {FLEXIBILITIES.map((f) => <option key={f} value={f}>{FLEXIBILITY_WORDS[f]}</option>)}
        </NativeSelect>
      </div>
      {guessed && w && (
        <div className="flex items-center gap-2 text-xs">
          <Badge variant="muted-soft" dot="bg-muted-foreground">{GUESSED}</Badge>
          <Button type="button" size="sm" variant="outline" onClick={() => onChange(confirmedWindow(w))}>{CONFIRM}</Button>
        </div>
      )}
    </div>
  )
}
